import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCopy, FiTrash2, FiCheck, FiClock } = FiIcons;

const JSONPreview = ({ formData, onClear }) => {
  const [copied, setCopied] = useState(false);
  const [jsonString, setJsonString] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const generateJSON = () => {
      const json = {
        model_config: {
          target_model: formData.targetModel || 'Google Veo 3.1',
          duration_seconds: formData.duration || 8,
          version: 'latest'
        },
        duration: formData.duration || 8, // Top level duration for compatibility
        scene_description: formData.sceneDescription || '',
        shot_composition: formData.composition || formData.customComposition || '',
        camera_movement: formData.cameraMovement || formData.customCameraMovement || '',
        visual_style: formData.visualStyle || formData.customVisualStyle || '',
        lighting: formData.lighting || formData.customLighting || '',
        environment: formData.environment || formData.customEnvironment || '',
        audio_cue: formData.audio || formData.customAudio || '',
        dialogue: formData.dialogue || '',
      };

      if (formData.selectedColorPalette) {
        json.color_palette = {
          name: formData.selectedColorPalette.name,
          colors: formData.selectedColorPalette.colors
        };
      }

      // Remove empty fields (except model config and duration)
      Object.keys(json).forEach(key => {
        if (key !== 'model_config' && key !== 'duration' && (json[key] === '' || (Array.isArray(json[key]) && json[key].length === 0))) {
          delete json[key];
        }
      });

      return JSON.stringify(json, null, 2);
    };

    const json = generateJSON();
    setJsonString(json);
    
    // Calculate word and character counts
    const words = json.split(/\s+/).filter(word => word.length > 0).length;
    setWordCount(words);
    setCharCount(json.length);
  }, [formData]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="clay-card p-8 h-fit lg:sticky lg:top-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold neon-text">
          ✨ Generated JSON Prompt
        </h3>
        <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full border border-gray-700">
          <SafeIcon icon={FiClock} className="text-green-400 text-xs" />
          <span className="text-xs text-white font-medium">{formData.duration || 8}s</span>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="bg-clay-pink p-4 rounded-clay-sm text-center flex-1">
          <div className="text-lg font-bold text-white">{wordCount}</div>
          <div className="text-xs text-gray-400">Words</div>
        </div>
        <div className="bg-clay-lavender p-4 rounded-clay-sm text-center flex-1">
          <div className="text-lg font-bold text-white">{charCount}</div>
          <div className="text-xs text-gray-400">Characters</div>
        </div>
        <div className="bg-clay-blue p-4 rounded-clay-sm text-center flex-1">
          <div className="text-lg font-bold text-white">1</div>
          <div className="text-xs text-gray-400">Sec Read</div>
        </div>
      </div>

      <div className="clay-code-block p-5 mb-6 relative rounded-clay">
        <pre className="text-green-400 text-sm overflow-auto max-h-96 whitespace-pre-wrap font-mono">
          {jsonString}
        </pre>
      </div>

      <div className="flex gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
          onClick={handleCopy}
          className="btn-primary clay-button flex-1 py-4 px-4 font-semibold flex items-center justify-center gap-2 highlight-hover"
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-sm" />
          {copied ? 'Copied!' : 'Copy JSON Prompt'}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
          onClick={onClear}
          className="clay-button flex-1 py-4 px-4 font-semibold flex items-center justify-center gap-2 text-gray-400 highlight-hover"
        >
          <SafeIcon icon={FiTrash2} className="text-sm" />
          Clear All
        </motion.button>
      </div>
    </motion.div>
  );
};

export default JSONPreview;